import { useState } from "react";
import dayjs from "dayjs";
import { db } from "../../lib/api";
import { invalidate, useQuery } from "../../lib/hooks";
import { useRefData } from "../../lib/refdata";
import { useSession } from "../../lib/session";
import { PageHead } from "../../components/Shell";
import { Empty, ErrorBox, Field, Loading, Panel, Select, Sheet, fmtShort, useToast } from "../../components/ui";
import { Columns, HBars, Kpi, THIS_YEAR } from "../../components/charts";
import { PeriodFilter, usePeriod } from "./Dashboards";
import { Pager, usePaged } from "../../components/Pager";

const BLANK = { posted_on: dayjs().format("YYYY-MM-DD"), platform: "", post_type: "", pillar: "", topic: "", link: "", likes: "", comments: "", shares: "", saves: "", new_followers: "", dms_leads: "", notes: "" };
const NUMS = ["likes", "comments", "shares", "saves", "new_followers", "dms_leads"] as const;

export default function Content() {
  const p = usePeriod();
  const ref = useRefData();
  const { can } = useSession();
  const toast = useToast();
  const [edit, setEdit] = useState<any | null>(null);
  const [err, setErr] = useState<Error | null>(null);
  const dash = useQuery(`dash:content:${p.from}:${p.to}`, () => db.rpc<any>("dash_content", { p_from: p.from, p_to: p.to }));
  const posts = useQuery(`content:list:${p.from}:${p.to}`, () =>
    db.select("content_posts", { select: "*", posted_on: `gte.${p.from}`, and: `(posted_on.lte.${p.to})`, order: "posted_on.desc,created_at.desc", limit: 500 }));
  const canEdit = can("content", "edit");

  async function save() {
    setErr(null);
    try {
      const body: any = { ...edit };
      for (const k of NUMS) body[k] = body[k] === "" || body[k] == null ? null : Number(body[k]);
      for (const k of ["post_type", "pillar", "topic", "link", "notes"]) if (body[k] === "") body[k] = null;
      if (!body.platform) throw new Error("Choose a platform.");
      const id = body.id; delete body.id; delete body.created_at; delete body.owner_user_id;
      if (id) await db.update("content_posts", { id: `eq.${id}` }, body); else await db.insert("content_posts", body);
      invalidate("content:"); invalidate("dash:");
      setEdit(null); toast("Post saved");
    } catch (e) { setErr(e as Error); }
  }

  const paged = usePaged((posts.data ?? []) as any[], "content", 25);
  const d = dash.data;
  return (
    <>
      <PageHead title="Content log" sub="Every post you publish, with how it did. Counts toward your Posts habit."
        actions={canEdit && <button className="btn primary" onClick={() => setEdit({ ...BLANK })}>Log a post</button>} />
      <PeriodFilter />
      <ErrorBox error={dash.error || posts.error} />
      {!d ? <Loading rows={4} /> : (
        <div className="stack">
          <div className="kpis">
            <Kpi dark label="Posts" value={d.posts} actual={d.posts} prev={d.posts_prev} />
            <Kpi label="This week" value={d.this_week} unit={`of ${d.plan_week} planned`} actual={d.this_week} target={d.plan_week || undefined} />
            <Kpi label="Posting days" value={d.posting_days} />
            <Kpi label="Engagement" value={Number(d.engagement ?? 0).toLocaleString()} sub="Likes, comments, shares, saves" />
            <Kpi label="DMs / leads" value={d.leads} />
          </div>
          <div className="grid-2">
            <Panel title="Posts per week" hint="Last 12 weeks">
              <div className="panel-body">
                <Columns labels={(d.weeks ?? []).map((w: any) => dayjs(w.week_start).format("MMM D"))}
                  target={d.plan_week ? { value: d.plan_week, label: "Weekly plan" } : undefined}
                  series={[{ key: "p", label: "Posts", color: THIS_YEAR, values: (d.weeks ?? []).map((w: any) => w.posts) }]} />
              </div>
            </Panel>
            <Panel title="By platform" hint="Posts this week against your plan">
              <div className="table-wrap">
                <table className="data">
                  <thead><tr><th>Platform</th><th className="num">This week</th><th className="num">Plan</th><th className="num">Posts</th><th className="num">Engagement</th><th className="num">Leads</th></tr></thead>
                  <tbody>{(d.by_platform ?? []).filter((x: any) => x.active || x.posts).map((x: any) => (
                    <tr key={x.platform}><td>{x.platform}</td><td className="num" style={{ color: x.per_week && x.this_week >= x.per_week ? "var(--warm)" : undefined }}>{x.this_week}</td><td className="num muted">{x.per_week}/wk</td><td className="num">{x.posts}</td><td className="num">{x.engagement}</td><td className="num">{x.leads}</td></tr>
                  ))}</tbody>
                </table>
              </div>
            </Panel>
          </div>
          {(d.by_pillar ?? []).length > 0 && (
            <Panel title="Content pillars"><div className="panel-body"><HBars rows={d.by_pillar.map((x: any) => ({ label: x.pillar ?? "—", value: x.posts }))} color="var(--gold)" /></div></Panel>
          )}
          <Panel title="Posts">
            <div className="table-wrap">
              <table className="data">
                <thead><tr><th>Date</th><th>Platform</th><th>Type</th><th>Pillar</th><th>Topic</th><th className="num">Likes</th><th className="num">Comments</th><th className="num">Shares</th><th className="num">Saves</th><th className="num">New followers</th><th className="num">DMs / leads</th></tr></thead>
                <tbody>
                  {(posts.data ?? []).length === 0 && <tr><td colSpan={11}><Empty title="No posts logged in this period">{canEdit ? "Use “Log a post” after you publish." : null}</Empty></td></tr>}
                  {paged.view.map((x: any) => (
                    <tr key={x.id} data-click onClick={() => canEdit && setEdit({ ...BLANK, ...Object.fromEntries(Object.entries(x).map(([k, v]) => [k, v ?? ""])) })}>
                      <td className="nowrap">{fmtShort(x.posted_on)}</td><td>{x.platform}</td><td>{x.post_type}</td><td>{x.pillar}</td>
                      <td>{x.link ? <a href={x.link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>{x.topic || "Open"}</a> : x.topic}</td>
                      {NUMS.map((k) => <td key={k} className="num">{x[k] ?? ""}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager p={paged} noun="posts" />
          </Panel>
        </div>
      )}
      {edit && (
        <Sheet title={edit.id ? "Edit post" : "Log a post"} onClose={() => setEdit(null)}
          footer={<>
            {edit.id && <button className="btn danger" onClick={async () => { await db.remove("content_posts", { id: `eq.${edit.id}` }); invalidate("content:"); invalidate("dash:"); setEdit(null); }}>Delete</button>}
            <span style={{ flex: 1 }} />
            <button className="btn" onClick={() => setEdit(null)}>Cancel</button>
            <button className="btn primary" onClick={save}>Save</button>
          </>}>
          <ErrorBox error={err} />
          <div className="form-grid">
            <Field label="Date"><input className="input" type="date" value={edit.posted_on} onChange={(e) => setEdit({ ...edit, posted_on: e.target.value })} /></Field>
            <Field label="Platform"><Select value={edit.platform} onChange={(v) => setEdit({ ...edit, platform: v })} options={ref.data?.list("Platforms") ?? []} placeholder="Choose…" /></Field>
            <Field label="Type"><Select value={edit.post_type} onChange={(v) => setEdit({ ...edit, post_type: v })} options={ref.data?.list("PostTypes") ?? []} /></Field>
            <Field label="Pillar"><Select value={edit.pillar} onChange={(v) => setEdit({ ...edit, pillar: v })} options={ref.data?.list("Pillars") ?? []} /></Field>
            <Field label="Topic" wide><input className="input" value={edit.topic} onChange={(e) => setEdit({ ...edit, topic: e.target.value })} /></Field>
            <Field label="Link" wide><input className="input" type="url" value={edit.link} onChange={(e) => setEdit({ ...edit, link: e.target.value })} /></Field>
            {NUMS.map((k) => (
              <Field key={k} label={k === "dms_leads" ? "DMs / leads" : k === "new_followers" ? "New followers" : k[0].toUpperCase() + k.slice(1)}>
                <input className="input" type="number" min={0} value={edit[k]} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} />
              </Field>
            ))}
            <Field label="Notes" wide><textarea className="textarea" value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} /></Field>
          </div>
        </Sheet>
      )}
    </>
  );
}
